import { type AccountMergeRequest } from './AccountMergeRequest'
import { action, computed } from 'mobx'
import { match } from '@/utils/pattern-match-util'
import { generateAvatarFallback } from '@/utils/image-util'
import { type MergeAccountsDialogViewModel } from '@/features/admin/account-linking/MergeAccountsDialog/MergeAccountsDialogViewModel'
import { type RejectMergeDialogViewModel } from '@/features/admin/account-linking/RejectMergeDialog/RejectMergeDialogViewModel'
import { type IgnoreMergeRequestDialogViewModel } from '@/features/admin/account-linking/IgnoreMergeRequestDialog/IgnoreMergeRequestDialogViewModel'

/**
 * Whether a merge request row shows its status, or its actions menu.
 */
export type StatusOrActions = 'Status' | 'Actions'

/**
 * The dialogs that act on a merge request.
 */
export interface AccountMergeRequestDialogs {
  readonly mergeAccountsDialogVm: MergeAccountsDialogViewModel
  readonly rejectMergeDialogVm: RejectMergeDialogViewModel
  readonly ignoreMergeRequestVm: IgnoreMergeRequestDialogViewModel
}

/**
 * Represents a specific account merge request item.
 */
export class AccountMergeRequestItemViewModel {
  constructor(
    private readonly _mergeRequest: AccountMergeRequest,
    private readonly _getStatusOrActions: () => StatusOrActions,
    private readonly _hubspotPortalId: string,
    private readonly _dialogs: AccountMergeRequestDialogs,
  ) {}

  @computed
  get displayName() {
    return this._mergeRequest.displayName
  }

  @computed
  get avatar() {
    return this._mergeRequest.picture
  }

  @computed
  get avatarFallback() {
    return generateAvatarFallback(this.displayName)
  }

  @computed
  get email() {
    return this._mergeRequest.email
  }

  @computed
  get primaryAccountId() {
    return this._mergeRequest.primaryAccountId
  }

  @computed
  get duplicateAccountId() {
    return this._mergeRequest.duplicateAccountId
  }

  @computed
  get ticketId() {
    return this._mergeRequest.ticketId
  }

  @computed
  get displayStatus() {
    return match(this._mergeRequest.status, {
      Requested: () => 'Merge requested',
      Rejected: () => 'Request rejected',
      Linked: () => 'Accounts merged',
      Ignored: () => 'Request ignored',
    })
  }

  @computed
  get statusOrActions() {
    return this._getStatusOrActions()
  }

  @computed
  get dateCreated() {
    return this._mergeRequest.dateCreated
  }

  @computed
  get id() {
    return this._mergeRequest.id
  }

  @computed
  get hubspotLink() {
    return `https://app.hubspot.com/contacts/${this._hubspotPortalId}/ticket/${this.ticketId}`
  }

  /**
   * Asks the user to merge the accounts.
   */
  @action.bound
  merge() {
    void this._dialogs.mergeAccountsDialogVm.prompt(this)
  }

  /**
   * Asks the user to reject the merge request.
   */
  @action.bound
  reject() {
    void this._dialogs.rejectMergeDialogVm.prompt(this)
  }

  /**
   * Asks the user to ignore the merge request.
   */
  @action.bound
  ignore() {
    void this._dialogs.ignoreMergeRequestVm.prompt(this)
  }
}
